import { CatalogPermissionService } from '@aipb/gulliver/catalog/auth';
import {
  MultiImageDownloadChecked,
  MultiImageDownloadRepository,
  SearchRepository,
  SearchService
} from '@aipb/gulliver/catalog/data-access';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';
import { CustomRecordItem, ResultListMode, SearchItem } from '@aipb/gulliver/catalog/model';
import { createDetailCompositeKey, isDropbox } from '@aipb/gulliver/catalog/util';
import { DropboxRepository } from '@aipb/gulliver/dropbox/data-access';
import { isNil } from '@aipb/shared/util/other';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule } from '@jsverse/transloco';
import { UntilDestroy } from '@ngneat/until-destroy';
import isEqual from 'lodash-es/isEqual';
import { combineLatest, map, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { SortDialogService } from '../../dialogs';
import { ResultListModeToolbarComponent } from '../result-list-mode-toolbar/result-list-mode-toolbar.component';
import { ResultListPaginatorComponent } from '../result-list-paginator/result-list-paginator.component';
import { MatIconModule } from '@angular/material/icon';

interface ResultListToolbarVM {
  resultListMode: ResultListMode;
  active: boolean;
  icon: string;
  iconClass: string;
  downloadInProgress: boolean;
  label: string;
}

type MultiImageDownloadState = 'ALL_SELECTED' | 'PARTIALLY_SELECTED' | 'NONE_SELECTED';

function getMultiImageDownloadState(
  checked: MultiImageDownloadChecked[],
  searchItem: SearchItem,
  uuid: string
): MultiImageDownloadState {
  const currentPageItems = searchItem.search.recordList;

  let checkedCountFromPage = 0;

  currentPageItems.forEach((item) => {
    const checkedItem = checked.find((c) => c.id === createDetailCompositeKey(searchItem.coreId, item[uuid]));

    if (checkedItem) {
      checkedCountFromPage++;
    }
  });

  if (checkedCountFromPage === currentPageItems.length) {
    return 'ALL_SELECTED';
  }

  if (checkedCountFromPage === 0) {
    return 'NONE_SELECTED';
  }

  return 'PARTIALLY_SELECTED';
}

@UntilDestroy()
@Component({
  selector: 'cat-result-list-toolbar',
  templateUrl: './result-list-toolbar.component.html',
  styleUrls: ['./result-list-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ResultListModeToolbarComponent,
    MatButtonModule,
    MatIconModule,
    TranslocoModule,
    ResultListPaginatorComponent
  ]
})
export class ResultListToolbarComponent implements OnChanges {
  @Input() searchItem: SearchItem;
  @Output() pageChange = new EventEmitter<number>();

  pageUnselectedIcon = 'check_box_outline_blank';
  pageSelectedIcon = 'check_box';
  pagePartlySelectedIcon = 'check_box';

  searchRepository = inject(SearchRepository);
  multiImageDownloadRepository = inject(MultiImageDownloadRepository);
  vm$: Observable<ResultListToolbarVM> = this.initViewModel();
  private searchService = inject(SearchService);
  private catalogEnv = inject(CatalogEnvService);
  private cdr = inject(ChangeDetectorRef);
  private sortDialogService = inject(SortDialogService);
  private catalogPermission = inject(CatalogPermissionService);
  private dropboxRepository = inject(DropboxRepository);

  private _label = '';

  get label(): string {
    return this._label;
  }

  get hasDownloadBatchRole(): boolean {
    return this.catalogPermission.hasDownloadBatchRole;
  }

  private _isSortDefault = false;

  get isSortDefault(): boolean {
    return this._isSortDefault;
  }

  get resultListModeEnabled(): boolean {
    return this.catalogEnv.env.enableResultListMode;
  }

  get resultListModes(): ResultListMode[] {
    return this.catalogEnv.env.resultListModes;
  }

  ngOnChanges(): void {
    this.initIsSortDefault();
    this.initLabel(this.searchItem.search.pageNum);
    this.vm$ = this.initViewModel();

    const top = document.getElementById('result-list-top-area');

    if (top) {
      top.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }

  onSort(): void {
    this.sortDialogService
      .open$({
        searchId: this.searchItem.id,
        coreId: this.searchItem.coreId
      })
      .pipe(finalize(this.cdr.detectChanges))
      .subscribe();
  }

  onChangeResultListMode(mode: ResultListMode): void {
    this.searchService.setResultListMode(mode);
  }

  onCheckAll() {
    const getImageUrl = (item: CustomRecordItem): string => {
      let image = item.header.images.length > 0 ? item.header.images[0].thumb || item.header.images[0].large : null;
      if (isDropbox(item.GulliverRecType)) {
        image = this.dropboxRepository.getImage(item[this.catalogEnv.env.castor.UUID]);
        return 'data:image/png;base64,' + image;
      }

      return image;
    };

    const checked = this.multiImageDownloadRepository.getChecked();

    const state = getMultiImageDownloadState(checked, this.searchItem, this.catalogEnv.env.castor.UUID);

    if (state === 'ALL_SELECTED') {
      checked
        .filter((c) =>
          this.searchItem.search.recordList.some(
            (item) => c.id === createDetailCompositeKey(this.searchItem.coreId, item[this.catalogEnv.env.castor.UUID])
          )
        )
        .forEach((item) => {
          this.multiImageDownloadRepository.uncheck(item);
        });
    }

    if (state === 'PARTIALLY_SELECTED' || state === 'NONE_SELECTED') {
      //this.multiImageDownloadRepository.che
      this.searchItem.search.recordList
        .map((r) => ({
          id: createDetailCompositeKey(this.searchItem.coreId, r.header.id),
          image: getImageUrl(r),
          title: r.GulliverTabName,
          type: r.GulliverRecType
        }))
        .forEach((item) => {
          this.multiImageDownloadRepository.addChecked(item);
        });
    }
  }

  private initLabel(page: number): void {
    const pageSize = this.searchItem.search.pageSize;
    const resultCount = this.searchItem.search.found;
    const pageNum = isNil(page) ? this.searchItem.search.pageNum : page;
    const from = resultCount > 0 ? pageNum * pageSize + 1 : 0;
    const to = (pageNum + 1) * pageSize > resultCount ? resultCount : (pageNum + 1) * this.searchItem.search.pageSize;

    this._label = `${from} - ${to}`;
  }

  private initIsSortDefault(): void {
    if (!this.searchItem) {
      this._isSortDefault = true;
      return;
    }

    this._isSortDefault = isEqual(this.searchItem.search_sort, this.catalogEnv.env.sort);
  }

  private initViewModel() {
    return combineLatest({
      resultListMode: this.searchRepository.resultListMode$,
      active: this.multiImageDownloadRepository.active$,
      icon: this.multiImageDownloadRepository.checked$.pipe(
        map((checked) => {
          const state = getMultiImageDownloadState(checked, this.searchItem, this.catalogEnv.env.castor.UUID);

          if (state === 'ALL_SELECTED') {
            return this.pageSelectedIcon;
          }

          if (state === 'NONE_SELECTED') {
            return this.pageUnselectedIcon;
          }

          return this.pagePartlySelectedIcon;
        })
      ),
      iconClass: this.multiImageDownloadRepository.checked$.pipe(
        map((checked) => {
          const state = getMultiImageDownloadState(checked, this.searchItem, this.catalogEnv.env.castor.UUID);

          if (state === 'ALL_SELECTED') {
            return 'fill';
          }

          return '';
        })
      ),
      downloadInProgress: this.multiImageDownloadRepository.downloadInProgress$,
      label: this.multiImageDownloadRepository.checked$.pipe(
        map((checked) => {
          const state = getMultiImageDownloadState(checked, this.searchItem, this.catalogEnv.env.castor.UUID);

          if (state === 'ALL_SELECTED') {
            return 'catalog.result_list.uncheck_all';
          }

          return 'catalog.result_list.check_all';
        })
      )
    });
  }
}
